// WorkflowCard: a `Workflow` tool call drawn as the background workflow it
// started (Chief of Staff mode). A workflow reports itself through task
// events only (`workflow_event`: name, status, latest summary, usage), which
// the reducer folds into `toolCall.workflow`; this card draws that.
//
// One line, like SubagentCard (plan
// design/2026-10-26-chief-of-staff-agents-panel.md, T8). Its agents are listed
// in the right panel's Agents section, not here: inside AgentChat a click
// opens the panel at this workflow (DelegatedAgentsContext `showWorkflow`).
// "n of m agents" comes from the chat's one shared run poll (`runs`), else
// from counts the runner reported, else is left out.

import React, { memo } from 'react'
import { Box } from '@mui/material'
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import type { ToolCallInfo, WorkflowInfo } from '../types.js'
import { DelegatedLine } from './SubagentCard.js'
import { useDelegatedAgents } from './DelegatedAgentsContext.js'
import { workflowAgentCount } from './AgentsSection.js'
import { CARD_SX } from './surface.js'

function workflowOf(toolCall: ToolCallInfo): WorkflowInfo {
  if (toolCall.workflow) return toolCall.workflow
  const input = toolCall.input || {}
  return {
    name: typeof input.name === 'string' ? input.name : undefined,
    status: toolCall.status === 'running' ? 'running'
      : toolCall.status === 'error' ? 'failed'
      : toolCall.status === 'cancelled' ? 'stopped'
      : 'completed',
    agents: [],
  }
}

function WorkflowCard({ toolCall }: { toolCall: ToolCallInfo }) {
  const api = useDelegatedAgents()
  const wf = workflowOf(toolCall)
  const run = wf.runId ? api?.runs.get(wf.runId) : undefined
  const progress = workflowAgentCount(run, wf.status)
    ?? (wf.agentsTotal ? `${wf.agentsDone ?? 0} of ${wf.agentsTotal} ${wf.agentsTotal === 1 ? 'agent' : 'agents'}` : undefined)
  const toolCallId = toolCall.id

  return (
    <Box data-testid="workflow-card" data-state={wf.status} sx={{ ...CARD_SX, mt: 1 }}>
      <DelegatedLine
        testId="workflow-card-header"
        onClick={api ? () => api.showWorkflow(toolCallId) : undefined}
        icon={<AccountTreeOutlinedIcon sx={{ fontSize: 16 }} />}
        kind="Workflow"
        title={wf.name || 'Workflow'}
        status={wf.status}
        progress={progress}
        summary={wf.summary}
        usage={wf.usage}
      />
    </Box>
  )
}

export default memo(WorkflowCard)
